import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { RequestOptions, Headers, Response } from '@angular/http';
import { List, Map } from 'immutable';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { ConfigRequest } from '../models/config/config-request';
import { ConfigResponse } from '../models/config/config-response';
import { ConfigLookupType } from '../models/config/config-lookup-type';
import { PropertyConfigLookup } from '../models/config/property-config-lookup';
import { AgencyConfigLookup } from '../models/config/agency-config-lookup';
import { ConfigLevelType } from '../models/config/config-level-type';
import { ResponseUtilService } from '../../shared/utils/response-util.service';
import { ConsoleLogSubscriberService } from '../../shared/log/console-log-subscriber.service';
import { LogMessage, LogLevel } from '../../shared/log/log.service';
import { AppAuthHttpService } from '../../http/app.auth.http.service';
import { CountryConfigLookup } from '../models/config/country-config-lookup';

@Injectable()
export class GroupPayConfigHttpService {

  private configLookupUrl: string = this.environment.baseUrl + '/api/lookup/config';
  private configLookupOoivUrl: string = this.environment.baseUrl + '/api/lookup/config/ooiv';

  constructor(private http: AppAuthHttpService,
              private environment: EnvironmentConfiguration,
              private responseUtilService: ResponseUtilService,
              private consoleLogSubscriber: ConsoleLogSubscriberService) {
  }

  getGroupPayConfig(request: ConfigRequest): Observable<ConfigResponse | Response> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.configLookupUrl, JSON.stringify(request.toJS()), options)
      .map((response) => {
        let json = null;
        try {
          json = response.json();
        } catch (error) {
          this.consoleLogSubscriber.log(new LogMessage(LogLevel.DEBUG, error));
        }
        return new ConfigResponse(json);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getGroupPayConfigBulk(requests: ConfigRequest[]): Observable<ConfigResponse[] | Response> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.configLookupUrl + '/bulk', JSON.stringify(requests), options)
      .map((response: Response) => {
        let json = null;
        try {
          json = response.json();
        } catch (error) {
          this.consoleLogSubscriber.log(new LogMessage(LogLevel.DEBUG, error));
        }
        return json;
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getGroupPayOoivConfig(request: ConfigRequest): Observable<ConfigResponse | Response> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.configLookupOoivUrl, JSON.stringify(request.toJS()), options)
      .map((response) => {
        let json = null;
        try {
          json = response.json();
        } catch (error) {
          this.consoleLogSubscriber.log(new LogMessage(LogLevel.DEBUG, error));
        }
        return new ConfigResponse(json);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getGroupPayConfigByValues(key: ConfigLookupType, chain: string, brand: string, propertyId: string, agencyId: string):
      Observable<ConfigResponse | Response> {
    return this.getGroupPayConfig(this.buildConfigRequest(key, chain, brand, propertyId, agencyId));
  }

  getGroupPayConfigByValuesBulk(requests: any[]): Observable<ConfigResponse[] | Response> {
    let configRequests: ConfigRequest[] = [];
    requests.forEach((request) => {
      configRequests.push(
        this.buildConfigRequest(
          request.key,
          request.chain,
          request.brand,
          request.propertyId,
          request.agencyId
        )
      );
    });

    return this.getGroupPayConfigBulk(configRequests);
  }

  getGroupPayOoivConfigByValues(key: ConfigLookupType, agencyId: string, agencyCountry: string, vendorCountry: string):
      Observable<ConfigResponse | Response> {
    return this.getGroupPayOoivConfig(this.buildConfigOoivRequest(key, agencyId, agencyCountry, vendorCountry));
  }

  getAllGroupPayConfig(request: ConfigRequest): Observable<Map<ConfigLookupType, ConfigResponse> | Response> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.configLookupUrl + '/all', JSON.stringify(request.toJS()), options)
      .map(response => {
        const result = [];
        const json = response.json();
        for (let property in json) {
          if (json.hasOwnProperty(property)) {
            const key = ConfigLookupType[property];
            const value = new ConfigResponse({
              config: ConfigLookupType[json[property].config],
              value: json[property].value,
              level: ConfigLevelType[json[property].level]
            });
            result.push([key, value]);
          }
        }
        return Map<ConfigLookupType, ConfigResponse>(result);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getAllGroupPayConfigByValues(chain: string,
                               brand: string,
                               propertyId: string,
                               agencyId: string): Observable<Map<ConfigLookupType, ConfigResponse> | Response> {
    return this.getAllGroupPayConfig(this.buildConfigRequest(ConfigLookupType.ALL, chain, brand, propertyId, agencyId));
  }



  buildConfigRequest(key: ConfigLookupType, chain: string, brand: string, propertyId: string, agencyId: string): ConfigRequest {

    let propertyConfigLookup = null;
    let agencyConfigLookup = null;
    let countryConfigLookup = null;

    if (chain != null || brand != null || propertyId != null) {
      propertyConfigLookup = new PropertyConfigLookup({
        chain: chain,
        brand: brand,
        propertyId: propertyId
      });
    }

    if (agencyId != null) {
      agencyConfigLookup = new AgencyConfigLookup({ agencyId: agencyId });
    }

    return new ConfigRequest({
      config: key,
      property: propertyConfigLookup,
      agency: agencyConfigLookup,
      country: countryConfigLookup
    });
  }

  buildConfigOoivRequest(key: ConfigLookupType, agencyId: string, agencyCountry: string, vendorCountry): ConfigRequest {

    let propertyConfigLookup = null;
    let agencyConfigLookup = null;
    let countryConfigLookup = null;

    if (agencyId != null) {
      agencyConfigLookup = new AgencyConfigLookup({ agencyId: agencyId });
    }

    if (agencyCountry != null || vendorCountry != null) {
      countryConfigLookup = new CountryConfigLookup({ agencyCountry: agencyCountry, vendorCountry: vendorCountry });
    }

    return new ConfigRequest({
      config: key,
      property: propertyConfigLookup,
      agency: agencyConfigLookup,
      country: countryConfigLookup
    });
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/grouppay-config.http.service.ts